import { useMemo } from "react"; // Importing useMemo hook from React for memoizing values
import { useRecoilState } from "recoil"; // Importing Recoil hooks for state management

import { CurrentScreenState, InstanceDetailsState, WebComponentConfigState } from "global-states"; // Importing global state atoms/selectors from Recoil
import { useNetwork } from "./network"; // Importing custom network hook
import { API_URL } from "constants/api-url"; // Importing API URL constants
import GlobalInstanceManager from "instance-manger";

export const useInstance = () => {
  const [currentScreen, setCurrentScreen] = useRecoilState(CurrentScreenState); // Recoil value for current screen state
  const [webComponentConfig, setWebComponentConfig] = useRecoilState(WebComponentConfigState)

  const { env, instance_id } = GlobalInstanceManager.webcomponentProps ?? {}; // Destructuring environment and instance ID from configuration

  const { get: fetchInstanceDetails, loading: instanceDetailsLoading } =
    useNetwork(); // Destructuring network fetch function and loading state from custom network hook

  const [iDetails, setIDetails] = useRecoilState(InstanceDetailsState); // Recoil state for instance details

  // Function to fetch instance details by ID
  const getInstanceDetails = async (
    id: string,
    customUrl?: string
  ): Promise<void | any> => {
    if (!id) {
      return {
        message: "instance_id is required", // Return error message if instance ID is not provided
      };
    }
    const resp = await fetchInstanceDetails(
      `${API_URL.AUTHENTICATION}/${id}`,
      customUrl
    ); // Fetch instance details from API
    if (resp?.data) {
      setIDetails(resp.data);
      return resp;
    } // Update Recoil state if response data is valid and not default
    return {
      statusCode: 500,
      message: "Something Went Wrong",
    }; // Return the response
  };

  const instanceDetails = useMemo(() => iDetails, [iDetails]); // Memoize instance details to avoid unnecessary re-renders

  const currentMethod = useMemo(() => {
    if (iDetails.signUp?.scope.includes("phone")) {
      return "phone";
    } else if (iDetails.signUp?.scope.includes("email")) {
      return "email";
    }
    return "";
  }, [iDetails]) as "email" | "phone";

  // Return object containing various instance-related properties and functions
  return {
    getInstanceDetails,
    instanceDetailsLoading,
    instanceDetails,
    currentScreen,
    env,
    instance_id,
    setCurrentScreen,
    currentMethod,
    webComponentConfig, 
    setWebComponentConfig
  };
};
